import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../../services/userService';

interface Trainer {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  accountStatus: string;
  joinedDate: string;
  profilePhotoUrl?: string | null;
  createdAt?: string;
}

interface TrainersTabProps {
  gymId: string;
}

const TrainersTab: React.FC<TrainersTabProps> = ({ gymId }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch gym trainers
  const { data: trainersData = [], isLoading } = useQuery({
    queryKey: ['gymTrainers', gymId],
    queryFn: () => userService.getGymTrainers(gymId),
    enabled: !!gymId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0, // Always refetch
  });

  const trainers: Trainer[] = Array.isArray(trainersData) ? trainersData : [];

  const filteredTrainers = trainers.filter(trainer =>
    trainer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalItems = filteredTrainers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTrainers = filteredTrainers.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 4;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 1; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500 bg-opacity-20 text-green-400';
      case 'Pending':
        return 'bg-yellow-500 bg-opacity-20 text-yellow-400';
      case 'Suspended':
        return 'bg-red-500 bg-opacity-20 text-red-400';
      default:
        return 'bg-gray-500 bg-opacity-20 text-gray-400';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-gray-400">Loading trainers...</div>
      </div>
    );
  }

  if (trainers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-6xl mb-4">👨‍💼</div>
        <h3 className="text-xl font-semibold text-white mb-2">No Trainers Found</h3>
        <p className="text-gray-400 text-center max-w-md">
          This gym doesn't have any trainers assigned yet. Trainers will appear here when they are assigned to this gym.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 text-gray-500" size={20} />
          <input
            type="text"
            placeholder="Search trainers by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-[#23F47C]"
          />
        </div>
      </div>


      {/* Trainers Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Trainers List</h2>
          
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Trainer</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Email Address</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Phone Number</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Account Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Joined Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTrainers.map((trainer) => (
                <tr key={trainer._id} className="border-b border-gray-800 hover:bg-gray-800 bg-opacity-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                        {trainer.profilePhotoUrl ? (
                          <img
                            src={trainer.profilePhotoUrl}
                            alt={trainer.firstName}
                            className="w-10 h-10 rounded-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className="text-gray-400 text-sm font-medium">
                            {trainer.firstName.charAt(0)}{trainer.lastName.charAt(0)}
                          </span>
                        )}
                      </div>
                      <span className="text-white font-medium">{trainer.firstName} {trainer.lastName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-300">{trainer.email}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-300">{trainer.phone || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(trainer.accountStatus)}`}>
                      {trainer.accountStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-300">{trainer.joinedDate}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button 
                        onClick={() => navigate(`/trainers/${trainer._id}`)}
                        className="p-2 text-gray-400 hover:text-[#23F47C] transition-all"
                        title="View Trainer Details"
                      >
                        <Eye size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
            <div className="text-gray-400 text-sm">
              Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} trainers
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={20} />
              </button>

              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                    currentPage === page
                      ? 'bg-[#23F47C] text-gray-900'
                      : 'border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C]'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainersTab;
